import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled from 'styled-components';
import { rgba } from 'polished';

import Sizes from '../definitions/Sizes';
import Colours from '../definitions/Colours';


function CalculationBox({
    isHighlightBox = false,
    subtitle,
    calculationLines = [],
}) {
    return (
        <Container
            className={classNames(
                'rh-pt-0_5',
                CALCULATION_BOX_STYLE[isHighlightBox ? 'totalBox' : 'roundedBox'],
            )}
            isHighlightBox={isHighlightBox}
            data-test-name={`calculation-box-${isHighlightBox ? 'highlight' : 'standard'}`}
        >
            <If condition={subtitle}>
                <p className={classNames(CALCULATION_BOX_STYLE.horizontalIndent, CALCULATION_BOX_STYLE.subtitle)}>
                    <span className="rh-fg-yuzu rh-text-l rh-mr-0_25">
                        {'•'}
                    </span>
                    <span>
                        {subtitle}
                    </span>
                </p>
            </If>

            <dl className="definition-list rh-display-grid rh-text-s rh-mt-0 rh-mb-0">
                <For
                    each="line"
                    of={calculationLines}
                >
                    <dt
                        className={classNames(
                            CALCULATION_BOX_STYLE.dt,
                            CALCULATION_BOX_STYLE[line.isTotal ? 'dlChildrenIsTotal' : 'dlChildrenStandard'],
                            {
                                'weight-medium': line.isEmphasized,
                                [CALCULATION_BOX_STYLE.subtotalRow]: line.isTotal && !isHighlightBox,
                                [CALCULATION_BOX_STYLE.totalRow]: line.isTotal && isHighlightBox,
                            })
                        }
                    >
                        {line.term}
                        <If condition={line.operator}>
                            <span className={CALCULATION_BOX_STYLE.operator}>
                                {line.operator}
                            </span>
                        </If>
                    </dt>
                    <dd
                        className={classNames(
                            CALCULATION_BOX_STYLE[line.isTotal ? 'dlChildrenIsTotal' : 'dlChildrenStandard'],
                            {
                                [CALCULATION_BOX_STYLE.subtotalRow]: line.isTotal && !isHighlightBox,
                                [CALCULATION_BOX_STYLE.totalRow]: line.isTotal && isHighlightBox,
                            }
                        )}
                    >
                        <output
                            className={classNames({
                                'weight-medium': line.isEmphasized,
                            })}
                        >
                            {line.definition}
                        </output>
                    </dd>
                </For>
            </dl>
        </Container>
    );
}

CalculationBox.propTypes = {
    isHighlightBox: PropTypes.bool,
    subtitle: PropTypes.node,
    calculationLines: PropTypes.arrayOf(
        PropTypes.shape({
            term: PropTypes.node,
            operator: PropTypes.string,
            definition: PropTypes.node,
            isEmphasized: PropTypes.bool,
        }),
    ),
};

const CALCULATION_BOX_STYLE = {
    roundedBox: 'rh-border-color-stone rh-border-width-1px rh-border-style-solid rh-border-radius-8px rh-mt-0_5',
    totalBox: 'rh-border-color-yuzu rh-border-width-1px rh-border-style-solid rh-border-radius-8px rh-mt-0_5',
    subtitle: 'rh-display-flex rh-text-2xs rh-my-0 rh-align-items-center weight-medium rh-fg-stone-dark',
    horizontalIndent: 'rh-mx-1',    // corresponds to dlChildren’s rh-pl-1
    subtotalRow: 'rh-bg-stone-lightest rh-border-top-color-stone rh-border-width-top-1px rh-border-style-solid',
    totalRow: 'rh-bg-yuzu-lightest rh-border-top-color-yuzu rh-border-width-top-1px rh-border-style-solid',
    dlChildrenStandard: 'rh-pl-1 rh-py-0_25 rh-pr-0_5',
    dlChildrenIsTotal: 'rh-pl-1 rh-py-1 rh-pr-0_5 rh-mt-0_5',
    dt: 'rh-display-inline-flex rh-justify-space-between',  // for optional operator child
    operator: 'operator rh-display-inline-flex rh-justify-content-center rh-align-items-center',
};


const Container = styled.div`
    .definition-list {
        grid-template-columns: 1fr 34%;

        dt,
        dd {
            .operator {
                padding: 0 2px;
                width: 0.7rem;
                border-radius: 25%;
                font-size: ${Sizes.FONTS.XS};

                // background changes to match box colour
                ${({ isHighlightBox }) => `background-color: ${rgba(Colours[isHighlightBox ? 'YUZU' : 'STONE'], 0.3)}`};
            }
        }

        dt {
            border-bottom-left-radius: 7px;
        }

        dd {
            margin-left: 0; // browser default override

            border-bottom-right-radius: 7px;
        }
    }
`;


export default CalculationBox;
